import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, User, Building2, Mail, Lock, Phone, UserCircle, CheckCircle, CreditCard } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
type CardMode = 'login' | 'cadastro';

// Mask functions
const formatCPF = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  return numbers.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};
const formatCNPJ = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 14);
  return numbers.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};
const formatPhone = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  if (numbers.length <= 10) {
    return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  }
  return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};
const cleanCPF = (value: string): string => value.replace(/\D/g, '');

const validateCPF = (cpf: string): boolean => {
  const numbers = cpf.replace(/\D/g, '');
  
  if (numbers.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(numbers)) return false;
  
  const digits = numbers.split('').map(Number);
  
  // Primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += digits[i] * (10 - i);
  }
  let remainder = sum % 11;
  const firstVerifier = remainder < 2 ? 0 : 11 - remainder;
  if (digits[9] !== firstVerifier) return false;
  
  // Segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += digits[i] * (11 - i);
  }
  remainder = sum % 11;
  const secondVerifier = remainder < 2 ? 0 : 11 - remainder;
  
  return digits[10] === secondVerifier;
};

const validatePhone = (phone: string): boolean => {
  const numbers = phone.replace(/\D/g, '');
  
  // Deve ter 10 (fixo) ou 11 (celular) dígitos
  if (numbers.length !== 10 && numbers.length !== 11) return false;
  
  // DDD deve estar entre 11 e 99
  const ddd = parseInt(numbers.slice(0, 2), 10);
  if (ddd < 11 || ddd > 99) return false;
  
  // Celular deve começar com 9 no terceiro dígito (após DDD)
  if (numbers.length === 11 && numbers[2] !== '9') return false;
  
  // Rejeita números com todos os dígitos do telefone iguais
  const phoneDigits = numbers.slice(2);
  if (/^(\d)\1+$/.test(phoneDigits)) return false;
  
  return true;
};

const validateCNPJ = (cnpj: string): boolean => {
  const numbers = cnpj.replace(/\D/g, '');
  
  // Deve ter 14 dígitos
  if (numbers.length !== 14) return false;
  
  // Rejeita CNPJs com todos os dígitos iguais
  if (/^(\d)\1{13}$/.test(numbers)) return false;
  
  const digits = numbers.split('').map(Number);
  
  // Primeiro dígito verificador (13º dígito)
  // Pesos: 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2
  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += digits[i] * firstWeights[i];
  }
  let remainder = sum % 11;
  const firstVerifier = remainder < 2 ? 0 : 11 - remainder;
  if (digits[12] !== firstVerifier) return false;
  
  // Segundo dígito verificador (14º dígito)
  // Pesos: 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += digits[i] * secondWeights[i];
  }
  remainder = sum % 11;
  const secondVerifier = remainder < 2 ? 0 : 11 - remainder;
  
  return digits[13] === secondVerifier;
};

export default function LoginPage() {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    user,
    loading: authLoading
  } = useAuth();

  // Handle OAuth return and profile creation
  const handleOAuthReturn = async (currentUser: typeof user, pendingUserType: string) => {
    try {
      if (pendingUserType === 'candidate') {
        // Check if candidate profile already exists
        const {
          data: existingProfile
        } = await supabase.from('candidate_profiles').select('id, profile_completed').eq('user_id', currentUser!.id).maybeSingle();
        
        if (!existingProfile) {
          // Check for conflict with company profile
          const {
            data: companyProfile
          } = await supabase.from('company_profiles').select('id').eq('user_id', currentUser!.id).maybeSingle();
          if (companyProfile) {
            toast({
              title: "Conta já existe como Empresa",
              description: "Este email já está cadastrado como empresa. Faça login como empresa.",
              variant: "destructive"
            });
            await supabase.auth.signOut();
            localStorage.removeItem('pendingGoogleUserType');
            return;
          }

          // Open modal to collect additional info instead of creating profile with empty fields
          setPendingGoogleUser(currentUser);
          setGoogleInfoUserType('candidate');
          setGoogleInfoPhone("");
          setGoogleInfoCpf("");
          setGoogleInfoModalOpen(true);
          return;
        }
        
        // Profile already exists - normal login
        localStorage.removeItem('pendingGoogleUserType');
        localStorage.setItem('userContext', 'candidate');
        toast({
          title: "Login realizado!",
          description: "Bem-vindo de volta!"
        });
        navigate('/candidato/minha-conta', {
          replace: true
        });
      } else if (pendingUserType === 'company') {
        // Check if company profile already exists
        const {
          data: existingProfile
        } = await supabase.from('company_profiles').select('id, profile_completed').eq('user_id', currentUser!.id).maybeSingle();
        
        if (!existingProfile) {
          // Check for conflict with candidate profile
          const {
            data: candidateProfile
          } = await supabase.from('candidate_profiles').select('id').eq('user_id', currentUser!.id).maybeSingle();
          if (candidateProfile) {
            toast({
              title: "Conta já existe como Candidato",
              description: "Este email já está cadastrado como candidato. Faça login como candidato.",
              variant: "destructive"
            });
            await supabase.auth.signOut();
            localStorage.removeItem('pendingGoogleUserType');
            return;
          }

          // Open modal to collect additional info instead of creating profile with empty fields
          setPendingGoogleUser(currentUser);
          setGoogleInfoUserType('company');
          setGoogleInfoPhone("");
          setGoogleInfoCnpj("");
          setGoogleInfoModalOpen(true);
          return;
        }
        
        // Profile already exists - normal login
        localStorage.removeItem('pendingGoogleUserType');
        localStorage.setItem('userContext', 'company');
        toast({
          title: "Login realizado!",
          description: "Bem-vindo de volta!"
        });
        navigate('/empresa/minha-conta', {
          replace: true
        });
      }
    } catch (error) {
      console.error('Error handling OAuth return:', error);
      toast({
        title: "Erro ao configurar conta",
        description: "Ocorreu um erro. Tente novamente.",
        variant: "destructive"
      });
      localStorage.removeItem('pendingGoogleUserType');
    }
  };

  // Handle Google OAuth additional info form submission
  const handleGoogleInfoSubmit = async () => {
    if (!pendingGoogleUser || !googleInfoUserType) return;

    // Validate phone
    if (!validatePhone(googleInfoPhone)) {
      toast({
        title: "Telefone inválido",
        description: "Informe um telefone válido.",
        variant: "destructive"
      });
      return;
    }

    setGoogleInfoLoading(true);

    try {
      if (googleInfoUserType === 'candidate') {
        // Validate CPF
        const cpfNumbers = cleanCPF(googleInfoCpf);
        if (!validateCPF(cpfNumbers)) {
          toast({
            title: "CPF inválido",
            description: "Por favor, informe um CPF válido.",
            variant: "destructive"
          });
          setGoogleInfoLoading(false);
          return;
        }

        // Check if CPF already exists
        const { data: cpfExists } = await supabase.rpc('check_cpf_exists', { cpf_to_check: cpfNumbers });
        if (cpfExists) {
          toast({
            title: "CPF já cadastrado",
            description: "Este CPF já está vinculado a outra conta. Verifique seus dados ou faça login.",
            variant: "destructive"
          });
          setGoogleInfoLoading(false);
          return;
        }

        // Create candidate profile
        const { error: profileError } = await supabase.from('candidate_profiles').insert({
          user_id: pendingGoogleUser.id,
          full_name: pendingGoogleUser.user_metadata?.full_name || pendingGoogleUser.user_metadata?.name || '',
          phone: googleInfoPhone.replace(/\D/g, ''),
          cpf: cpfNumbers
        });

        if (profileError) {
          console.error('Error creating candidate profile:', profileError);
          toast({
            title: "Erro ao criar perfil",
            description: "Ocorreu um erro ao configurar sua conta. Tente novamente.",
            variant: "destructive"
          });
          setGoogleInfoLoading(false);
          return;
        }

        // Create user role
        await supabase.from('user_roles').insert({
          user_id: pendingGoogleUser.id,
          role: 'candidate'
        });

        setGoogleInfoModalOpen(false);
        localStorage.removeItem('pendingGoogleUserType');
        localStorage.setItem('userContext', 'candidate');
        toast({
          title: "Conta criada!",
          description: "Bem-vindo! Complete seu perfil para continuar."
        });
        navigate('/candidato/minha-conta', { replace: true });

      } else if (googleInfoUserType === 'company') {
        // Validate CNPJ
        const cnpjNumbers = googleInfoCnpj.replace(/\D/g, '');
        if (!validateCNPJ(cnpjNumbers)) {
          toast({
            title: "CNPJ inválido",
            description: "Por favor, informe um CNPJ válido.",
            variant: "destructive"
          });
          setGoogleInfoLoading(false);
          return;
        }

        // Check if CNPJ already exists
        const { data: cnpjExists } = await supabase.rpc('check_cnpj_exists', { cnpj_to_check: cnpjNumbers });
        if (cnpjExists) {
          toast({
            title: "CNPJ já cadastrado",
            description: "Este CNPJ já está vinculado a outra conta. Verifique seus dados ou faça login.",
            variant: "destructive"
          });
          setGoogleInfoLoading(false);
          return;
        }

        // Create company profile with initial credits
        const { error: profileError } = await supabase.from('company_profiles').insert({
          user_id: pendingGoogleUser.id,
          company_name: pendingGoogleUser.user_metadata?.full_name || pendingGoogleUser.user_metadata?.name || 'Minha Empresa',
          phone: googleInfoPhone.replace(/\D/g, ''),
          cnpj: cnpjNumbers,
          responsible_name: '',
          is_verified: true,
          credits: 5,
          credits_expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        });

        if (profileError) {
          console.error('Error creating company profile:', profileError);
          toast({
            title: "Erro ao criar perfil",
            description: "Ocorreu um erro ao configurar sua conta. Tente novamente.",
            variant: "destructive"
          });
          setGoogleInfoLoading(false);
          return;
        }

        // Create user role
        await supabase.from('user_roles').insert({
          user_id: pendingGoogleUser.id,
          role: 'company'
        });

        setGoogleInfoModalOpen(false);
        localStorage.removeItem('pendingGoogleUserType');
        localStorage.setItem('userContext', 'company');
        toast({
          title: "Conta criada!",
          description: "Bem-vindo! Complete seu perfil para continuar."
        });
        navigate('/empresa/minha-conta', { replace: true });
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao criar sua conta. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setGoogleInfoLoading(false);
    }
  };

  // Redirect logged-in users and handle OAuth return
  useEffect(() => {
    if (!authLoading && user) {
      // Check if this is an OAuth return with pending user type
      const pendingGoogleUserType = localStorage.getItem('pendingGoogleUserType');
      if (pendingGoogleUserType) {
        // Process OAuth profile creation
        handleOAuthReturn(user, pendingGoogleUserType);
        return;
      }

      // Fallback: Check if user has a profile - handles edge cases where localStorage was lost
      const checkUserProfile = async () => {
        const { data: candidateProfile } = await supabase
          .from('candidate_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        const { data: companyProfile } = await supabase
          .from('company_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (!candidateProfile && !companyProfile) {
          // User has no profile - this shouldn't happen normally
          // Sign out and show message to try again
          await supabase.auth.signOut();
          toast({
            title: "Sessão incompleta",
            description: "Por favor, faça login novamente.",
            variant: "destructive"
          });
          return;
        }
        
        // Normal redirect for already logged-in users
        const userContext = localStorage.getItem('userContext');
        if (userContext === 'company' || companyProfile) {
          localStorage.setItem('userContext', 'company');
          navigate('/empresa/dashboard', { replace: true });
        } else {
          localStorage.setItem('userContext', 'candidate');
          navigate('/', { replace: true });
        }
      };
      
      checkUserProfile();
    }
  }, [user, authLoading, navigate]);

  // Card mode state
  const [candidateMode, setCandidateMode] = useState<CardMode>('login');
  const [companyMode, setCompanyMode] = useState<CardMode>('login');

  // Candidate login state
  const [candidateEmail, setCandidateEmail] = useState("");
  const [candidatePassword, setCandidatePassword] = useState("");
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateGoogleLoading, setCandidateGoogleLoading] = useState(false);

  // Candidate registration state
  const [candidateRegName, setCandidateRegName] = useState("");
  const [candidateRegCpf, setCandidateRegCpf] = useState("");
  const [candidateRegEmail, setCandidateRegEmail] = useState("");
  const [candidateRegPhone, setCandidateRegPhone] = useState("");
  const [candidateRegPassword, setCandidateRegPassword] = useState("");
  const [candidateRegConfirmPassword, setCandidateRegConfirmPassword] = useState("");
  const [candidateRegTerms, setCandidateRegTerms] = useState(false);
  const [candidateRegLoading, setCandidateRegLoading] = useState(false);
  const [candidateEmailSent, setCandidateEmailSent] = useState(false);

  // Company login state
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyPassword, setCompanyPassword] = useState("");
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyGoogleLoading, setCompanyGoogleLoading] = useState(false);

  // Company registration state
  const [companyRegName, setCompanyRegName] = useState("");
  const [companyRegCnpj, setCompanyRegCnpj] = useState("");
  const [companyRegEmail, setCompanyRegEmail] = useState("");
  const [companyRegPhone, setCompanyRegPhone] = useState("");
  const [companyRegResponsible, setCompanyRegResponsible] = useState("");
  const [companyRegPassword, setCompanyRegPassword] = useState("");
  const [companyRegConfirmPassword, setCompanyRegConfirmPassword] = useState("");
  const [companyRegTerms, setCompanyRegTerms] = useState(false);
  const [companyRegLoading, setCompanyRegLoading] = useState(false);
  const [companyEmailSent, setCompanyEmailSent] = useState(false);

  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // Google OAuth additional info modal state
  const [googleInfoModalOpen, setGoogleInfoModalOpen] = useState(false);
  const [googleInfoUserType, setGoogleInfoUserType] = useState<'candidate' | 'company' | null>(null);
  const [googleInfoPhone, setGoogleInfoPhone] = useState("");
  const [googleInfoCpf, setGoogleInfoCpf] = useState("");
  const [googleInfoCnpj, setGoogleInfoCnpj] = useState("");
  const [googleInfoLoading, setGoogleInfoLoading] = useState(false);
  const [pendingGoogleUser, setPendingGoogleUser] = useState<typeof user>(null);

  const handleCandidateLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setCandidateLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email: candidateEmail,
        password: candidatePassword
      });
      if (error) {
        toast({
          title: "Erro no login",
          description: error.message === "Invalid login credentials" ? "Email ou senha incorretos" : error.message,
          variant: "destructive"
        });
        setCandidateLoading(false);
        return;
      }
      if (data.user) {

        const {
          data: candidateProfile
        } = await supabase.from('candidate_profiles').select('id, profile_completed').eq('user_id', data.user.id).maybeSingle();
        if (!candidateProfile) {
          await supabase.auth.signOut();
          toast({
            title: "Acesso negado",
            description: "Esta conta não possui perfil de candidato. Use o login de empresa.",
            variant: "destructive"
          });
          setCandidateLoading(false);
          return;
        }
        localStorage.setItem('userContext', 'candidate');
        toast({
          title: "Login realizado!",
          description: "Bem-vindo de volta!"
        });

        // Redirect to profile page on first login
        if (!candidateProfile.profile_completed) {
          navigate('/candidato/minha-conta');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive"
      });
    }
    setCandidateLoading(false);
  };
  const handleCandidateRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidateRegTerms) {
      toast({
        title: "Termos de uso",
        description: "Você precisa aceitar os termos de uso para criar sua conta.",
        variant: "destructive"
      });
      return;
    }
    if (!validatePhone(candidateRegPhone)) {
      toast({
        title: "Telefone inválido",
        description: "Informe um telefone válido.",
        variant: "destructive"
      });
      return;
    }
    const cpfNumbers = cleanCPF(candidateRegCpf);
    if (!validateCPF(cpfNumbers)) {
      toast({
        title: "CPF inválido",
        description: "Por favor, informe um CPF válido.",
        variant: "destructive"
      });
      return;
    }
    if (candidateRegPassword !== candidateRegConfirmPassword) {
      toast({
        title: "Erro no cadastro",
        description: "As senhas não coincidem.",
        variant: "destructive"
      });
      return;
    }
    if (candidateRegPassword.length < 6) {
      toast({
        title: "Erro no cadastro",
        description: "A senha deve ter no mínimo 6 caracteres.",
        variant: "destructive"
      });
      return;
    }
    setCandidateRegLoading(true);
    try {
      // Verificar se email pertence a uma empresa
      const {
        data: isCompanyEmail
      } = await supabase.rpc('check_email_is_company', {
        email_to_check: candidateRegEmail
      });
      if (isCompanyEmail) {
        toast({
          title: "E-mail já cadastrado",
          description: "Este e-mail já está vinculado a uma conta de empresa. Use outro e-mail ou faça login como empresa.",
          variant: "destructive"
        });
        setCandidateRegLoading(false);
        return;
      }

      // Verificar se CPF já existe
      const {
        data: cpfExists
      } = await supabase.rpc('check_cpf_exists', {
        cpf_to_check: cpfNumbers
      });
      if (cpfExists) {
        toast({
          title: "CPF já cadastrado",
          description: "Este CPF já está vinculado a outra conta. Verifique seus dados ou faça login.",
          variant: "destructive"
        });
        setCandidateRegLoading(false);
        return;
      }

      // Verificar se email pertence a um candidato
      const {
        data: isCandidateEmail
      } = await supabase.rpc('check_email_is_candidate', {
        email_to_check: candidateRegEmail
      });
      if (isCandidateEmail) {
        toast({
          title: "E-mail já cadastrado",
          description: "Este e-mail já possui uma conta de candidato. Faça login para acessar.",
          variant: "destructive"
        });
        setCandidateRegLoading(false);
        return;
      }
      const {
        data: authData,
        error: authError
      } = await supabase.auth.signUp({
        email: candidateRegEmail,
        password: candidateRegPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
          data: {
            user_type: "candidate",
            full_name: candidateRegName,
            phone: candidateRegPhone.replace(/\D/g, ''),
            cpf: cpfNumbers
          }
        }
      });
      if (authError) {
        if (authError.message.includes("already registered")) {
          toast({
            title: "Erro no cadastro",
            description: "Este e-mail já está cadastrado. Faça login ou use outro e-mail.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Erro no cadastro",
            description: authError.message,
            variant: "destructive"
          });
        }
        setCandidateRegLoading(false);
        return;
      }
      if (authData.user) {
        setCandidateEmailSent(true);
      }
    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao criar sua conta. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setCandidateRegLoading(false);
    }
  };
  const handleCompanyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setCompanyLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email: companyEmail,
        password: companyPassword
      });
      if (error) {
        toast({
          title: "Erro no login",
          description: error.message === "Invalid login credentials" ? "Email ou senha incorretos" : error.message,
          variant: "destructive"
        });
        setCompanyLoading(false);
        return;
      }
      if (data.user) {

        const {
          data: companyProfile
        } = await supabase.from('company_profiles').select('id, is_verified, profile_completed').eq('user_id', data.user.id).maybeSingle();
        if (!companyProfile) {
          await supabase.auth.signOut();
          toast({
            title: "Acesso negado",
            description: "Esta conta não possui perfil de empresa. Use o login de candidato.",
            variant: "destructive"
          });
          setCompanyLoading(false);
          return;
        }
        localStorage.setItem('userContext', 'company');
        toast({
          title: "Login realizado!",
          description: "Bem-vindo de volta!"
        });

        // Redirect to profile page on first login
        if (!companyProfile.profile_completed) {
          navigate('/empresa/minha-conta');
        } else {
          navigate('/empresa/dashboard');
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive"
      });
    }
    setCompanyLoading(false);
  };
  const handleCompanyRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyRegTerms) {
      toast({
        title: "Termos de uso",
        description: "Você precisa aceitar os termos de uso para criar sua conta.",
        variant: "destructive"
      });
      return;
    }
    if (companyRegPassword !== companyRegConfirmPassword) {
      toast({
        title: "Erro no cadastro",
        description: "As senhas não coincidem.",
        variant: "destructive"
      });
      return;
    }
    if (companyRegPassword.length < 6) {
      toast({
        title: "Erro no cadastro",
        description: "A senha deve ter no mínimo 6 caracteres.",
        variant: "destructive"
      });
      return;
    }
    if (!validatePhone(companyRegPhone)) {
      toast({
        title: "Telefone inválido",
        description: "Informe um telefone válido.",
        variant: "destructive"
      });
      return;
    }

    // Validar CNPJ
    const cnpjNumbers = companyRegCnpj.replace(/\D/g, '');
    if (!validateCNPJ(cnpjNumbers)) {
      toast({
        title: "CNPJ inválido",
        description: "Por favor, informe um CNPJ válido.",
        variant: "destructive"
      });
      return;
    }

    setCompanyRegLoading(true);
    try {
      // Verificar se email pertence a um candidato
      const {
        data: isCandidateEmail
      } = await supabase.rpc('check_email_is_candidate', {
        email_to_check: companyRegEmail
      });
      if (isCandidateEmail) {
        toast({
          title: "E-mail já cadastrado",
          description: "Este e-mail já está vinculado a uma conta de candidato. Use outro e-mail ou faça login como candidato.",
          variant: "destructive"
        });
        setCompanyRegLoading(false);
        return;
      }

      // Verificar se email já é de empresa
      const {
        data: isCompanyEmail
      } = await supabase.rpc('check_email_is_company', {
        email_to_check: companyRegEmail
      });
      if (isCompanyEmail) {
        toast({
          title: "E-mail já cadastrado",
          description: "Este e-mail já possui uma conta de empresa. Faça login para acessar.",
          variant: "destructive"
        });
        setCompanyRegLoading(false);
        return;
      }

      // Verificar se CNPJ já existe
      const {
        data: cnpjExists
      } = await supabase.rpc('check_cnpj_exists', {
        cnpj_to_check: cnpjNumbers
      });
      if (cnpjExists) {
        toast({
          title: "CNPJ já cadastrado",
          description: "Este CNPJ já está vinculado a outra conta. Verifique seus dados ou faça login.",
          variant: "destructive"
        });
        setCompanyRegLoading(false);
        return;
      }
      const {
        data: authData,
        error: authError
      } = await supabase.auth.signUp({
        email: companyRegEmail,
        password: companyRegPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
          data: {
            user_type: "company",
            company_name: companyRegName,
            cnpj: companyRegCnpj.replace(/\D/g, ''),
            phone: companyRegPhone.replace(/\D/g, ''),
            responsible_name: companyRegResponsible
          }
        }
      });
      if (authError) {
        if (authError.message.includes("already registered")) {
          toast({
            title: "Erro no cadastro",
            description: "Este e-mail já está cadastrado. Faça login ou use outro e-mail.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Erro no cadastro",
            description: authError.message,
            variant: "destructive"
          });
        }
        setCompanyRegLoading(false);
        return;
      }
      if (authData.user) {
        setCompanyEmailSent(true);
      }
    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao criar sua conta. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setCompanyRegLoading(false);
    }
  };
  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast({
        title: "Email obrigatório",
        description: "Por favor, informe seu email.",
        variant: "destructive"
      });
      return;
    }
    setForgotPasswordLoading(true);
    try {
      const {
        data: emailCheck
      } = await supabase.rpc('check_email_exists', {
        check_email: forgotPasswordEmail
      });
      if (!emailCheck || emailCheck.length === 0) {
        toast({
          title: "Email não encontrado",
          description: "Este email não está cadastrado na plataforma.",
          variant: "destructive"
        });
        setForgotPasswordLoading(false);
        return;
      }
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: "Erro",
          description: error.message || "Erro ao enviar email de recuperação.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Email enviado!",
          description: "Verifique sua caixa de entrada para redefinir sua senha."
        });
        setForgotPasswordOpen(false);
        setForgotPasswordEmail("");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao enviar o email.",
        variant: "destructive"
      });
    }
    setForgotPasswordLoading(false);
  };
  const openForgotPassword = (email: string) => {
    setForgotPasswordEmail(email);
    setForgotPasswordOpen(true);
  };
  const handleGoogleLogin = async (userType: 'candidate' | 'company') => {
    const setLoading = userType === 'candidate' ? setCandidateGoogleLoading : setCompanyGoogleLoading;
    setLoading(true);
    try {
      // Save user type BEFORE redirect
      localStorage.setItem('pendingGoogleUserType', userType);

      // OAuth with explicit redirectTo to ensure we return to /entrar for profile creation
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/entrar`
        }
      });
      if (error) {
        toast({
          title: "Erro ao conectar com Google",
          description: error.message,
          variant: "destructive"
        });
        localStorage.removeItem('pendingGoogleUserType');
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao tentar fazer login com Google.",
        variant: "destructive"
      });
      localStorage.removeItem('pendingGoogleUserType');
    }
    setLoading(false);
  };
  const GoogleIcon = () => <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>;
  // Show loading while checking auth
  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }
  const renderCandidateCard = () => {
    if (candidateEmailSent) {
      return <Card className="border-2 border-primary/50">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-xl text-green-600">Verifique seu e-mail</CardTitle>
            <CardDescription>
              Enviamos um link de confirmação para <strong>{candidateRegEmail}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Não recebeu? Verifique sua pasta de spam.
            </p>
            <Button variant="outline" className="w-full" onClick={() => {
            setCandidateEmailSent(false);
            setCandidateMode('login');
          }}>
              Voltar para Login
            </Button>
          </CardContent>
        </Card>;
    }
    return <Card className="border-2 hover:border-primary/50 transition-colors">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-xl">Sou Candidato</CardTitle>
          <CardDescription>
            Acesse sua conta para buscar vagas e gerenciar seu currículo
          </CardDescription>
          <div className="flex gap-2 mt-4">
            <Button variant={candidateMode === 'login' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setCandidateMode('login')}>
              Login
            </Button>
            <Button variant={candidateMode === 'cadastro' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setCandidateMode('cadastro')}>
              Cadastro
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {candidateMode === 'login' ? <form onSubmit={handleCandidateLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="candidate-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="candidate-email" type="email" placeholder="seu@email.com" value={candidateEmail} onChange={e => setCandidateEmail(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="candidate-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <PasswordInput id="candidate-password" placeholder="••••••••" value={candidatePassword} onChange={e => setCandidatePassword(e.target.value)} className="pl-10" required />
                </div>
              </div>

              <Button type="button" variant="link" className="p-0 h-auto text-sm text-muted-foreground" onClick={() => openForgotPassword(candidateEmail)}>
                Esqueci minha senha
              </Button>

              <Button type="submit" className="w-full" disabled={candidateLoading}>
                {candidateLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando...
                  </> : "Entrar"}
              </Button>

              {/* Separador "ou" */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  
                </div>
              </div>

              {/* Botão Google */}
              <Button type="button" variant="outline" className="w-full" onClick={() => handleGoogleLogin('candidate')} disabled={candidateGoogleLoading}>
                {candidateGoogleLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GoogleIcon />}
                Entrar com Google
              </Button>
            </form> : <form onSubmit={handleCandidateRegister} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="candidate-reg-name">Nome Completo</Label>
                <div className="relative">
                  <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="candidate-reg-name" placeholder="Seu nome completo" value={candidateRegName} onChange={e => setCandidateRegName(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="candidate-reg-cpf">CPF</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="candidate-reg-cpf" placeholder="000.000.000-00" value={candidateRegCpf} onChange={e => setCandidateRegCpf(formatCPF(e.target.value))} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="candidate-reg-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="candidate-reg-email" type="email" placeholder="seu@email.com" value={candidateRegEmail} onChange={e => setCandidateRegEmail(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="candidate-reg-phone">Telefone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="candidate-reg-phone" type="tel" placeholder="(11) 99999-9999" value={candidateRegPhone} onChange={e => setCandidateRegPhone(formatPhone(e.target.value))} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="candidate-reg-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <PasswordInput id="candidate-reg-password" placeholder="Mínimo 6 caracteres" value={candidateRegPassword} onChange={e => setCandidateRegPassword(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="candidate-reg-confirm">Confirmar Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <PasswordInput id="candidate-reg-confirm" placeholder="Repita sua senha" value={candidateRegConfirmPassword} onChange={e => setCandidateRegConfirmPassword(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="flex items-start space-x-2 pt-2">
                <Checkbox id="candidate-terms" checked={candidateRegTerms} onCheckedChange={checked => setCandidateRegTerms(checked === true)} />
                <label htmlFor="candidate-terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                  Li e aceito os{" "}
                  <Link to="/termos" target="_blank" className="text-primary hover:underline font-medium">
                    termos de uso
                  </Link>
                </label>
              </div>
              <Button type="submit" className="w-full" disabled={candidateRegLoading}>
                {candidateRegLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando conta...
                  </> : "Criar Conta"}
              </Button>

              {/* Separador "ou" */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  
                </div>
              </div>

              {/* Botão Google */}
              <Button type="button" variant="outline" className="w-full" onClick={() => handleGoogleLogin('candidate')} disabled={candidateGoogleLoading}>
                {candidateGoogleLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GoogleIcon />}
                Cadastrar com Google
              </Button>
            </form>}
        </CardContent>
      </Card>;
  };
  const renderCompanyCard = () => {
    if (companyEmailSent) {
      return <Card className="border-2 border-orange-500/50">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-xl text-green-600">Verifique seu e-mail</CardTitle>
            <CardDescription>
              Enviamos um link de confirmação para <strong>{companyRegEmail}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Não recebeu? Verifique sua pasta de spam.
            </p>
            <Button variant="outline" className="w-full" onClick={() => {
            setCompanyEmailSent(false);
            setCompanyMode('login');
          }}>
              Voltar para Login
            </Button>
          </CardContent>
        </Card>;
    }
    return <Card className="border-2 hover:border-orange-500/50 transition-colors">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-orange-500/10 flex items-center justify-center">
            <Building2 className="h-8 w-8 text-orange-500" />
          </div>
          <CardTitle className="text-xl">Sou Empresa</CardTitle>
          <CardDescription>
            Acesse sua conta para publicar vagas e encontrar talentos
          </CardDescription>
          <div className="flex gap-2 mt-4">
            <Button variant={companyMode === 'login' ? 'default' : 'outline'} size="sm" className={`flex-1 ${companyMode === 'login' ? 'bg-orange-500 hover:bg-orange-600' : ''}`} onClick={() => setCompanyMode('login')}>
              Login
            </Button>
            <Button variant={companyMode === 'cadastro' ? 'default' : 'outline'} size="sm" className={`flex-1 ${companyMode === 'cadastro' ? 'bg-orange-500 hover:bg-orange-600' : ''}`} onClick={() => setCompanyMode('cadastro')}>
              Cadastro
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {companyMode === 'login' ? <form onSubmit={handleCompanyLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="company-email" type="email" placeholder="empresa@email.com" value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <PasswordInput id="company-password" placeholder="••••••••" value={companyPassword} onChange={e => setCompanyPassword(e.target.value)} className="pl-10" required />
                </div>
              </div>

              <Button type="button" variant="link" className="p-0 h-auto text-sm text-muted-foreground" onClick={() => openForgotPassword(companyEmail)}>
                Esqueci minha senha
              </Button>

              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={companyLoading}>
                {companyLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando...
                  </> : "Entrar"}
              </Button>

              {/* Separador "ou" */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  
                </div>
              </div>

              {/* Botão Google */}
              <Button type="button" variant="outline" className="w-full" onClick={() => handleGoogleLogin('company')} disabled={companyGoogleLoading}>
                {companyGoogleLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GoogleIcon />}
                Entrar com Google
              </Button>
            </form> : <form onSubmit={handleCompanyRegister} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="company-reg-name">Nome da Empresa</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="company-reg-name" placeholder="Nome da empresa" value={companyRegName} onChange={e => setCompanyRegName(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-reg-cnpj">CNPJ</Label>
                <Input id="company-reg-cnpj" placeholder="00.000.000/0000-00" value={companyRegCnpj} onChange={e => setCompanyRegCnpj(formatCNPJ(e.target.value))} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-reg-email">Email Corporativo</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="company-reg-email" type="email" placeholder="contato@empresa.com" value={companyRegEmail} onChange={e => setCompanyRegEmail(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-reg-phone">Telefone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="company-reg-phone" type="tel" placeholder="(11) 99999-9999" value={companyRegPhone} onChange={e => setCompanyRegPhone(formatPhone(e.target.value))} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-reg-responsible">Nome do Responsável</Label>
                <div className="relative">
                  <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="company-reg-responsible" placeholder="Seu nome" value={companyRegResponsible} onChange={e => setCompanyRegResponsible(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-reg-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <PasswordInput id="company-reg-password" placeholder="Mínimo 6 caracteres" value={companyRegPassword} onChange={e => setCompanyRegPassword(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-reg-confirm">Confirmar Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <PasswordInput id="company-reg-confirm" placeholder="Repita sua senha" value={companyRegConfirmPassword} onChange={e => setCompanyRegConfirmPassword(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="flex items-start space-x-2 pt-2">
                <Checkbox id="company-terms" checked={companyRegTerms} onCheckedChange={checked => setCompanyRegTerms(checked === true)} />
                <label htmlFor="company-terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                  Li e aceito os{" "}
                  <Link to="/termos" target="_blank" className="text-primary hover:underline font-medium">
                    termos de uso
                  </Link>
                </label>
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={companyRegLoading}>
                {companyRegLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cadastrando...
                  </> : "Criar Conta"}
              </Button>

              {/* Separador "ou" */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  
                </div>
              </div>

              {/* Botão Google */}
              <Button type="button" variant="outline" className="w-full" onClick={() => handleGoogleLogin('company')} disabled={companyGoogleLoading}>
                {companyGoogleLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GoogleIcon />}
                Cadastrar com Google
              </Button>
            </form>}
        </CardContent>
      </Card>;
  };
  return <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 container py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Entrar na plataforma</h1>
          <p className="text-muted-foreground">Escolha seu tipo de conta para fazer login ou cadastro</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto items-start">
          {renderCandidateCard()}
          {renderCompanyCard()}
        </div>
      </main>

      <Footer />

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recuperar senha</DialogTitle>
            <DialogDescription>
              Informe seu email para receber o link de recuperação de senha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input id="forgot-email" type="email" placeholder="seu@email.com" value={forgotPasswordEmail} onChange={e => setForgotPasswordEmail(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotPasswordOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleForgotPassword} disabled={forgotPasswordLoading}>
              {forgotPasswordLoading ? <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </> : "Enviar link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Google OAuth Additional Info Modal - Cannot be closed */}
      <Dialog open={googleInfoModalOpen} onOpenChange={() => {}}>
        <DialogContent 
          hideCloseButton
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Complete seu cadastro</DialogTitle>
            <DialogDescription>
              Para continuar, precisamos de algumas informações adicionais.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="google-phone">Telefone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="google-phone"
                  placeholder="(11) 99999-9999"
                  value={googleInfoPhone}
                  onChange={(e) => setGoogleInfoPhone(formatPhone(e.target.value))}
                  className="pl-10"
                />
              </div>
            </div>
            
            {googleInfoUserType === 'candidate' ? (
              <div className="space-y-2">
                <Label htmlFor="google-cpf">CPF</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="google-cpf"
                    placeholder="000.000.000-00"
                    value={googleInfoCpf}
                    onChange={(e) => setGoogleInfoCpf(formatCPF(e.target.value))}
                    className="pl-10"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="google-cnpj">CNPJ</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="google-cnpj"
                    placeholder="00.000.000/0000-00"
                    value={googleInfoCnpj}
                    onChange={(e) => setGoogleInfoCnpj(formatCNPJ(e.target.value))}
                    className="pl-10"
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handleGoogleInfoSubmit} 
              disabled={googleInfoLoading}
              className="w-full"
            >
              {googleInfoLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : "Continuar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>;
}